import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const features = [
  { icon: "ri-medal-line", key: "quality", color: "from-amber-500 to-amber-600" },
  { icon: "ri-gift-2-line", key: "packaging", color: "from-amber-400 to-orange-500" },
  { icon: "ri-edit-line", key: "personalized", color: "from-rose-500 to-rose-600" },
  { icon: "ri-truck-line", key: "delivery", color: "from-emerald-400 to-green-500" },
  { icon: "ri-building-line", key: "corporate", color: "from-slate-400 to-gray-500" },
];

export default function WhyChooseSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 md:py-28 bg-white relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-5 pointer-events-none"
        style={{ background: "radial-gradient(circle, #f43f5e, transparent)", transform: "translate(30%, -30%)" }} />
      <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full opacity-5 pointer-events-none"
        style={{ background: "radial-gradient(circle, #f59e0b, transparent)", transform: "translate(-30%, 30%)" }} />

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Image */}
          <div
            className="relative transition-all duration-800"
            style={{ opacity: visible ? 1 : 0, transform: visible ? "translateX(0)" : "translateX(-50px)", transitionDuration: "0.8s" }}
          >
            <div className="relative rounded-3xl overflow-hidden h-[500px]">
              <img
                src="https://readdy.ai/api/search-image?query=elegant%20gift%20shop%20interior%20with%20beautiful%20flower%20arrangements%20candles%20and%20gift%20boxes%20displayed%20on%20cream%20marble%20shelves%2C%20warm%20golden%20light%2C%20premium%20boutique%20aesthetic%2C%20luxurious%20and%20romantic%20atmosphere%2C%20soft%20pink%20tones&width=700&height=800&seq=why1&orientation=portrait"
                alt="Creative Hub Premium Quality"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-rose-900/30 to-transparent" />
            </div>

            {/* Floating stat cards */}
            <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-5 shadow-xl border border-rose-100"
              style={{ animation: "floatCard 4s ease-in-out infinite" }}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-amber-500 to-rose-600">
                  <i className="ri-heart-fill text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700" style={{ fontFamily: "'Playfair Display', serif" }}>2,500+</p>
                  <p className="text-xs text-gray-500">Happy Customers</p>
                </div>
              </div>
            </div>

            <div className="absolute -top-6 -left-6 bg-white rounded-2xl p-4 shadow-xl border border-amber-100"
              style={{ animation: "floatCard 5s ease-in-out infinite", animationDelay: "1s" }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500">
                  <i className="ri-star-fill text-white text-base"></i>
                </div>
                <div>
                  <p className="text-xl font-bold text-amber-700" style={{ fontFamily: "'Playfair Display', serif" }}>4.9/5</p>
                  <p className="text-xs text-gray-500">Average Rating</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Content */}
          <div
            className="transition-all duration-800"
            style={{ opacity: visible ? 1 : 0, transform: visible ? "translateX(0)" : "translateX(50px)", transitionDuration: "0.8s", transitionDelay: "0.2s" }}
          >
            <span className="inline-block text-amber-700 text-xs font-semibold uppercase tracking-widest mb-3">
              — Why Choose Us —
            </span>
            <h2
              className="text-4xl md:text-5xl font-bold text-stone-800 mb-4 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("why_title")}
            </h2>
            <p className="text-gray-600 text-lg mb-10">{t("why_subtitle")}</p>

            <div className="flex flex-col gap-6">
              {features.map((feat, i) => (
                <div
                  key={feat.key}
                  className="flex items-start gap-4 group"
                  style={{
                    opacity: visible ? 1 : 0,
                    transform: visible ? "translateX(0)" : "translateX(30px)",
                    transition: `all 0.6s ease`,
                    transitionDelay: `${0.4 + i * 0.1}s`,
                  }}
                >
                  <div className={`w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br ${feat.color} flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                    <i className={`${feat.icon} text-white text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 text-base mb-1">
                      {t(`why_${feat.key}`)}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      {t(`why_${feat.key}_desc`)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes floatCard {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
    </section>
  );
}